<script setup lang="ts">
import { watch } from 'vue'

import { chartColor, formatDecimal, useChartJs } from '@/shared/lib'

import type { PieChartProps } from './types'

const props = withDefaults(defineProps<PieChartProps>(), { height: 300 })

/** Evenly spaced hues, so any number of slices stays tellable apart. */
function sliceColours(count: number): string[] {
  return Array.from(
    { length: count },
    (_, index) => `hsl(${(200 + (index * 360) / count) % 360} 60% 62%)`
  )
}

const { element, render } = useChartJs({
  hasData: () => props.data.length > 0,
  draw: ({ Chart }, host) => {
    const total = props.data.reduce((sum, slice) => sum + slice.value, 0)

    return new Chart(host, {
      type: 'pie',
      data: {
        labels: props.data.map((slice) => slice.name),
        datasets: [
          {
            data: props.data.map((slice) => slice.value),
            backgroundColor: sliceColours(props.data.length),
            borderColor: chartColor('--el-bg-color'),
            borderWidth: 2
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              generateLabels: (chart) =>
                Chart.overrides.pie.plugins.legend.labels
                  .generateLabels(chart)
                  .map((label, index) => {
                    const value = props.data[index]?.value ?? 0
                    const share = total ? (value / total) * 100 : 0

                    return {
                      ...label,
                      text: `${label.text} ${share.toFixed(1)}%`
                    }
                  })
            }
          },
          tooltip: {
            callbacks: {
              label: (item) => {
                const share = total ? (Number(item.raw) / total) * 100 : 0

                return `${item.label}: ${formatDecimal(Number(item.raw))} (${share.toFixed(1)}%)`
              }
            }
          }
        }
      }
    })
  }
})

watch(() => props.data, render, { flush: 'post' })
</script>

<template>
  <div class="pie-chart" :style="{ height: `${height}px` }">
    <canvas ref="element" />
  </div>
</template>

<style lang="scss" scoped>
.pie-chart {
  position: relative;
}
</style>
